import { POStatus } from '@utility/constants/common';
import { brand } from '@utility/config';

export const ATTNHelpText = {
  invoice:
    'Here you can add any contacts within your organization and Vendors to this Invoice. Upon creation, all contacts added to ATTN section will be sent an email notification with the summary of the Invoice. You can choose to disable email notifications by unchecking the "Notify" checkbox.',
  purchaseOrder:
    'Here you can add any contacts within your organization and Supplier to this Purchase order. Upon creation, all contacts added to ATTN section will be sent an email notification with the summary of the Purchase order. You can choose to disable email notifications by unchecking the "Notify" checkbox.',
};

export const PurchaseOrderHelpText = {
  unAllocatedViewReason:
    'Status must be "Ready for Shipment" or "In Production" to show Unallocated Items',
};

export const ShipmentHelpText = {
  ETA: 'Estimated Arrival of Shipment',
  ETD: 'Estimated Departure of Shipment',
  originName:
    'Provide a clear origin name (e.g., supplier, Port of LA, or warehouse) to define where the shipment originates.',
  attnExisting:
    'Here you can add any contacts within your organization and Storage Provider to this Shipment. Upon creation, all contacts added to ATTN section will be sent an email notification with the summary of the Shipment.',
  attn: 'Here you can add any contacts within your organization and Storage Provider to this Shipment. Upon creation, all contacts added to ATTN section will be sent an email notification with the summary of the Shipment. You can choose to disable email notifications by unchecking the "Notify" checkbox.',
};

export const WholesalerDetailHelpText = {
  currency:
    'Choose wholesalers receiving currency to generate invoices in the receiving currency.',
};

export const StorageProviderHelpText = {
  autoUpload:
    'Turn this on to send tracking information to the marketplace as soon as the shipping label is created. If turned off, tracking will be sent only after the shipment is approved by Tactical. This lets you decide when your orders are marked as shipped.',
  typeTooltip:
    'Select this type in order to enable fulfillment options. A fulfillment center type can store and ship individual orders. Note: if fulfillment center type is selected but not warehouse type, you can not manage inventory as bulk cartons and all inventory must be in units. A storage provider can be both types.',
  warehouseTypeTooltip:
    'Select this type in order to enable carton management. A warehouse type can store bulk cartons and pallets. Note: if warehouse type is selected but not fulfillment center type, you can not store individual units and all inventory must be in boxes. A storage provider can be both types.',
  holdbackQtyTooltip:
    'This is a safety buffer. During fulfillment we check the threshold quantity field before processing an order. If the inventory drops below this level, this fulfillment provider will not be used to handle the fulfillment.',
  handlingTimeTooltip:
    'Amount of time it takes to prepare and dispatch a shipment',
  restockTooltip: 'Add a limit to the amount of units that can be stored.',
  orderCutOffTimeTooltip: 'Daily cutoff time for handling orders.',
  inboundBufferDayTooltip:
    'Select an inbound buffer time (or days to intake a shipment). Buffer times are used in calculating shipping and purchasing predictions.',
  outboundBufferDayTooltip:
    'Select an outbound buffer time (or days to prepare and dispatch a shipment). Buffer times are used in calculating shipping and purchasing predictions.',
  importTooltip: `These Google documents are active and data is 2-way binding with ${brand.name}, synced within a 1-2 minute window. This means any changes made to the google sheet will update your inventory on ${brand.name}. Any changes made on ${brand.name} will update the google sheet values. To save a copy of the report at any moment in time, simply export the sheet into your preferred file format.`,
  statementDescriptor:
    'Statement descriptor is what we show on the users bank statement for successful charges.',
  labelName:
    'The Sender Name will appear as the sender name on shipping labels fulfilled by this Storage Provider.',
  useLabelName: `If this is turned on, we will use the storage provider name as the sender name on the shipping labels. The default setting is the workspace name. If this option is checked but the Sender Name input is empty, we will use the Storage Provider name.`,
  amazonConnceted: (
    <span>
      Select this option if this warehouse is connected to Amazon for the{' '}
      <b>Ship to AWD</b> program. By selecting this, when Amazon automatically
      creates an FBA shipment for this warehouse as the ship-from location,
      {brand.name} will deduct inventory automatically from the warehouse.
    </span>
  ),
  quantityTypeInfo:
    'The quantity type field is to explicitly set the incoming quantity as units or carton count. The configuration may be uncertain in some 3PL integrations and this option allows you to override our default behavior.',
  quantityTypeInfoUpdate:
    'The quantity type field is to explicitly set the incoming quantity as units or carton count. The configuration may be uncertain in some 3PL integrations and this option allows you to override our default behavior. Once you update the field, please allow some time to re-sync the inventory in the selected quantity configuration.',
};

export const CatalogHelpText = {
  shipInBox:
    'If Ships in Product Packaging is selected, we will always ship the item in its own box as it is',
  carton: "A unique identifier for an item's specific box dimensions",
  moq: 'The Minimum Order Quantity(MOQ) is configured in the Supplier Page. You can navigate and update the MOQ by clicking on MOQ number/value.',
  shortTitle:
    'Short title is used as the default title when a marketplace, such as eBay or Google, imposes a character limit on product titles. If both a short title and a custom title are configured, we prioritize the custom title.',
  shipWithSignature:
    'A signature will be required upon delivery to ensure this order reaches to customer securely.',
  mailer:
    'Uses item’s exact dimensions to ensure carriers rate based on the solid frame, specifically designed for flat envelopes and mailer packages.',
  noSippingPreference:
    'Automatically selects the optimal box size for your order, applied to both single items and multiple units to maximize shipping efficiency.',
  singleFirstPackaging:
    'Adapts packaging based on order quantity: uses product packaging for single items, selects optimal boxes for multiple units or mixed orders to maximize shipping efficiency.',
  strappedBundle:
    'Items marked for this config are banded together for shipment. Label dims follow linear mailer sizing, stacking height for total depth. If only one item, it ships as-is using its own packaging or mailer',
  includeAllListings:
    'Includes all linked or matched listings associated with the master products. Enable this if you want to include every related listing in the results.',
};

export const SupplierItemsHelpText = {
  grossWeight: 'Total weight of contents including carton',
  netWeight: 'Total weight of contents excluding carton',
  multipleSupplierInfo: 'This product is produced by multiple suppliers.',
};

export const addressValidationText = {
  stateInfo:
    'Enter a 2-character state abbreviation (e.g., CA, NY) to prevent fulfillment issues.',
};

export const publicLinks = {
  amazonSpliShipment:
    'https://sellercentral.amazon.com/help/hub/reference/G201021870',
};

export const conflictHelpText = {
  resolve:
    'Once you input the UPC you need to tap this button to resolve the conflict. If you input a UPC of an existing item, it links this SKU to the respective item.',
  remove:
    'If an item does not require a UPC you can tap this button. This will assign an alternate identifier like ASIN or another 3rd party itemId as the global unique identifier. Note that some functions will not work as expected if you choose to have a non-UPC identifier like multi-channel marketplace syncing.',
  ignore:
    'For some marketplaces, such as Walmart, Google, and Etsy, an item requires a real and valid UPC, which cannot be ignored.',
};

export const HAZMAT_TEXT_1 =
  'Identify whether your shipment contains dangerous goods. It will be noted on the shipping label.';

export const HAZMAT_TEXT_2 = `This category includes items such as batteries, dry ice, flammable
                              liquids, aerosol sprays, air bags, ammunition, fireworks, gasoline,
                              lighters, lithium batteries, matches, nail polish, nail polish remover,
                              nitrogen-refrigerated liquid, paint, perfume, aerosols, camping stoves,
                              radioactive materials, solvents, and more.`;

export const HAZMAT_TEXT_3 = 'These items must ship in separate packages.';

export const PO_DOCUMENT_UPLOAD_TEXT = {
  [POStatus.pendingPayment]: {
    title: 'Initial Payment Receipt',
    text: 'The supplier has accepted the purchase order. Please upload initial payment documents for supplier review.',
  },
  [POStatus.pendingFinalPayment]: {
    title: 'Upload Final Payment Documents',
    text: 'The purchase order is complete. Please upload the final payment documents for supplier review.',
  },
};

export const SettingsHelpText = {
  useProviderName:
    'If this is turned on, we will use the storage provider name as the sender name on the shipping labels. The default setting is the workspace name.',
  autoReload: `With Auto-Reload, you can set a minimum balance threshold. If your wallet balance falls below this threshold, ${brand.name} will automatically add funds to bring your balance up to the target amount you choose. The difference between your current balance and the target amount will be charged to your default payment method every time the threshold is reached.`,
};

export const OrderSettings = {
  walletReloadThrehold:
    'The reload threshold is the value which triggers the automated reloading when your wallet balance first drops below this amount. Must be lower than reload amount.',
  fulfillmentPriority:
    'We will follow this hierarchy of providers when fulfilling marketplace orders. The highest priority providers will only be assigned if they have inventory',
  returnProcessing:
    'Return processing is the location where your returned orders will be processed. If it is a warehouse you manage, you will be in charge of discarding, refurbishing or re-storing the returned items. For FedEx, the return address must match the send-from address. UPS and USPS support dynamic return addresses.',
  defaultProvider: `When orders are imported into ${brand.name} but fulfilled outside our system, we are unable to determine which storage provider is responsible for fulfilling the respective order. By configuring this setting, we automatically attribute any unassigned orders to the respective provider by crediting its internal sales velocity. Therefore, ongoing replenishment forecasting is accurately capturing all points of fulfillment.`,
  manualMode:
    'If this is turned on orders stay stuck on Pending Shipment for any marketplace orders and manual orders',
  blockPartialFulfillment:
    'Enable this setting in order to block automatic fulfillment of orders if any one of the line items is Out of Stock, i.e. automatic fulfillment will only fulfill orders when 100% of items are in stock in any location.',
  fulfillmentThreshold:
    'If Manual Mode is ON (i.e Auto Fulfillment is enabled), marketplace orders will be automatically fulfilled only if the order cost meets or exceeds the Auto Fulfillment Threshold specified in this field.',
  fulfillmentExcludeMarketplace:
    'If Manual Mode is disabled (i.e., Auto Fulfillment is enabled), all marketplaces, except those explicitly excluded from this field will be automatically fulfilled only if the order cost meets or exceeds the Auto Fulfillment Threshold specified in this field.',
  autoPrint:
    'If Auto Print is enabled, labels will automatically print when the order is fulfilled.',
};

export const OrderTable = {
  returnStatus: 'This is the marketplace order id as synced from the source',
  remainingItems: 'Some items might be assigned to other packages.',
  all: 'Displays all orders that have been returned by customers.',
  refurbish:
    'Orders that require refurbishment or repairs before they can be resold or restocked.',
  completed:
    'Orders that have gone through the return process and have been successfully resolved or closed.',
};

export const termsObject = {
  cog: 'Cost of Goods',
  principal: 'Principal',
  organic_sales: 'Organic Sales',
  ads_sales: 'Ads Sales',
  f_b_a_per_unit_fulfillment_fee: 'FBA Shipping Fee',
  commission: 'Product Sales Commission',
  tax: 'Sales Tax for non-Amazon items',
  marketplace_facilitator_tax_principal: 'FBA Items Tax',
  promotion: 'Product Promotion Fee',
  shipping_charge:
    'Shipping Charge from other Service Provider (e.g., Deliverr)',
  shopify_commission: 'Shopify Sales Commission',
  discount: 'Discount Offered',
  gift_wrap: 'Gift Wrap Cost',
};

export const GroupMOQ = {
  subMOQHeader: `Sub MOQ is the minimum quantity required for each individual item within a group or combined MOQ. Each item has its own sub-MOQ, ensuring that you meet the supplier's requirements for that specific product. If there is no specific minimum quantity for an item, you can enter 0 in the input field`,
  collectiveMOQ: `Collective MOQ signifies the total quantity that must be reached across all items within a combined MOQ group to initiate production or order fulfillment. It's the cumulative sum of individual sub-MOQs for each item, ensuring that the overall group quantity is met for production to proceed.`,
  groupMOQModal: `Combined MOQ represents the minimum quantity of products that a supplier requires for a group of items. This combined quantity is shared among these items, allowing you to meet the supplier's requirements for the entire group rather than each item individually.`,
};

export const assignItemsStrings = {
  itemInConflict:
    'This item is in conflict and cannot be assigned to a supplier. Please resolve the conflict before proceeding.',
};

export const OrderHelpText = {
  cancelWholeOrder:
    'The entire order, including both products and shipping, will be canceled. The customer receives a full refund for both the items and the shipping costs.',
  cancelOrderFulfillment:
    'Only the Shipment will be stopped but the order will be still retained.',
};

export const CardsText = {
  secondaryCardTextInfo:
    'While the ACH payment is processing, another ACH request cannot be made. However, if a secondary default payment method is set to a credit card, it will be used in the interim. The secondary credit card payment method has its own threshold and auto-reload settings, and it can be used without adjusting the default method until the ACH credits clear.',
};
